import axios from "axios";
import SupplierEvent, { ListSupplierEvent, SupplierEventType } from "@/features/SupplierEvents/SupplierEvent";
import SupplierEventRequestModel from "@/features/SupplierEvents/RequestModels/SupplierEventRequestModel";
import Country from "@/features/SupplierEvents/Country";
import SupplierEventProductUploadResult from "@/features/SupplierEvents/SupplierEventProductUploadResult";

export interface SupplierEventResponse {
    supplierEvents: ListSupplierEvent[];
    totalSupplierEvents: number;
}

export class SupplierEventService {
    private supplierEventsURL = "/api/v1/supplierevents";
    private referenceDataURL = "/api/v1/referencedata";
    private countriesCache: Country[] = [];
    private countriesCacheExpiration: Date = new Date(0);

    async getSupplierEvents(query: object | null = null): Promise<SupplierEventResponse> {
        const path = `${this.supplierEventsURL}/`;

        const response = await axios.get(path, {
            params: query,
        });
        return response.data as SupplierEventResponse;
    }

    async getSupplierEvent(id: string): Promise<SupplierEvent> {
        const response = await axios.get(this.supplierEventsURL + "/" + id);
        return response.data as SupplierEvent;
    }

    async createSupplierEvent(supplierEvent: SupplierEventRequestModel): Promise<string> {
        const response = await axios.post(this.supplierEventsURL, supplierEvent);

        return response.data;
    }

    async updateSupplierEvent(id: string, supplierEvent: SupplierEventRequestModel): Promise<string> {
        const response = await axios.put(this.supplierEventsURL + "/" + id, supplierEvent);

        return response.data;
    }

    async getSupplierEventDocument(id: string, docArchiveID: string, filename: string): Promise<void> {
        try {
            // Add an HTML anchor element to the page linking to the document to download
            const anchor = document.createElement("a");
            document.body.appendChild(anchor);
            const requestPath = this.supplierEventsURL + "/" + id + "/document/" + docArchiveID;

            // Get document from server and prepare to download it to browser
            const blob = (await axios.get(requestPath, { responseType: "blob" })).data as Blob;
            const objectUrl = window.URL.createObjectURL(blob);

            anchor.href = objectUrl;
            anchor.download = filename;

            // Click the link to download the document
            anchor.click();

            // Clean up the link
            window.URL.revokeObjectURL(objectUrl);
            document.body.removeChild(anchor);
        } catch (err) {
            console.log(err);
            alert(`Error downloading ${filename}`);
        }
    }

    async getCountries(): Promise<Country[]> {
        const now = new Date();

        if (now >= this.countriesCacheExpiration) {
            //the entry is expired
            console.log("Refreshing countries cache");
            //We don't have the data or it is expired, fetch it from the server.
            const path = `${this.referenceDataURL}/countries`;
            const response = await axios.get(path);
            this.countriesCache = response.data.sort((c1: Country, c2: Country) => c1.name?.localeCompare(c2.name) ?? 0);
            //Reset the cache expiration for 24 hours in the future.
            this.countriesCacheExpiration = new Date(now.getTime() + 86400000); //24 hours in milliseconds (configurable?)
        }

        return this.countriesCache;
    }

    async uploadDocumentForSupplierEventProductProcessing(
        document: File,
        supplierID: string,
        supplierEventType: SupplierEventType,
        progressCallback: (progress: number) => void
    ): Promise<SupplierEventProductUploadResult> {
        const response = await axios.postForm(
            this.supplierEventsURL + "/processproducts",
            {
                file: document,
            },
            {
                onUploadProgress: function (progressEvent: ProgressEvent) {
                    progressCallback(progressEvent.loaded / progressEvent.total);
                },
                params: {
                    supplierID: supplierID,
                    supplierEventType: supplierEventType,
                },
            }
        );

        return response.data as SupplierEventProductUploadResult;
    }
}
